import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Loader2,
  RefreshCw,
  Search,
  Send,
  ShieldAlert,
  Sparkles,
} from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';
import { formatMoney } from '../i18n/orgFormat';

type ClaimRow = {
  id: string;
  billingId: string;
  invoiceNumber: string;
  patient: string;
  mrn: string;
  payer: string;
  providerId: string | null;
  provider: string;
  issueCode: string;
  issueSummary: string;
  strategy: 'doc-fix' | 'coding-fix' | 'appeal' | 'payer-follow-up';
  status: 'queued' | 'retrying' | 'submitted' | 'manual_review' | 'recovered' | 'dismissed';
  retryCount: number;
  confidence: number;
  billed: number;
  paid: number;
  balance: number;
  ageDays: number;
  pastDueDays: number;
  nextFollowUpAt: string | null;
  lastActionAt: string | null;
  latestActionSummary: string | null;
  resolvedAt: string | null;
  recoveredAmount: number;
  lastPaymentDate: string | null;
  serviceType: string;
};

type ClaimsPayload = {
  generatedAt: string;
  summary: {
    queueCount: number;
    retryingCount: number;
    submittedCount: number;
    manualReviewCount: number;
    recoveredCount: number;
    atRiskAmount: number;
    recoveredAmount30d: number;
  };
  charts: {
    recoveryTrend: Array<{ label: string; recovered: number; atRisk: number }>;
    strategyMix: Array<{ strategy: string; count: number; amount: number }>;
    payerRisk: Array<{ payer: string; amount: number }>;
    statusMix: Array<{ status: string; count: number }>;
  };
  options: {
    payers: string[];
    providers: Array<{ id: string; name: string }>;
  };
  rows: ClaimRow[];
};

const strategyLabel: Record<ClaimRow['strategy'], string> = {
  'doc-fix': 'Documentation fix',
  'coding-fix': 'Coding fix',
  appeal: 'Formal appeal',
  'payer-follow-up': 'Payer follow-up',
};

const strategyTone: Record<ClaimRow['strategy'], string> = {
  'doc-fix': 'bg-sky-50 text-sky-700',
  'coding-fix': 'bg-violet-50 text-violet-700',
  appeal: 'bg-rose-50 text-rose-700',
  'payer-follow-up': 'bg-amber-50 text-amber-700',
};

const statusTone: Record<ClaimRow['status'], string> = {
  queued: 'bg-amber-50 text-amber-700 border-amber-100',
  retrying: 'bg-sky-50 text-sky-700 border-sky-100',
  submitted: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  manual_review: 'bg-rose-50 text-rose-700 border-rose-100',
  recovered: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  dismissed: 'bg-slate-100 text-slate-600 border-slate-200',
};

function formatCurrency(value: number) {
  return formatMoney(value, { maximumFractionDigits: 0 });
}

function formatCurrencyCompact(value: number) {
  return formatMoney(value, { notation: 'compact', maximumFractionDigits: 1 });
}

function formatPercent(value: number) {
  return `${Math.round((value || 0) * 100)}%`;
}

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function chartBase(title: string): Highcharts.Options {
  return {
    chart: {
      backgroundColor: 'transparent',
      style: { fontFamily: 'inherit' },
    },
    title: {
      text: title,
      align: 'left',
      style: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#0f172a',
      },
    },
    credits: { enabled: false },
    xAxis: {
      labels: { style: { color: '#64748b' } },
      lineColor: '#e2e8f0',
      tickColor: '#e2e8f0',
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: '#e2e8f0',
      labels: { style: { color: '#64748b' } },
    },
    legend: {
      itemStyle: { color: '#475569' },
    },
    tooltip: {
      shared: true,
      backgroundColor: '#0f172a',
      borderRadius: 12,
      style: { color: '#f8fafc' },
    },
  };
}

export default function ClaimsRetryEnginePage() {
  const { fetchApi, fetchJson } = useApiClient();
  const { tr } = useI18n();

  const [data, setData] = useState<ClaimsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [acting, setActing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [strategyFilter, setStrategyFilter] = useState('');
  const [payerFilter, setPayerFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState('');

  const loadData = useCallback(
    async (soft = false) => {
      if (soft) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set('months', '12');
        params.set('activeOnly', String(activeOnly));
        if (statusFilter) params.set('status', statusFilter);
        if (strategyFilter) params.set('strategy', strategyFilter);
        if (payerFilter) params.set('payer', payerFilter);
        if (providerFilter) params.set('providerId', providerFilter);
        const payload = await fetchJson<ClaimsPayload>(`/billing/claims-retry?${params.toString()}`);
        setData(payload);
      } catch (err: any) {
        setError(err?.message || tr('Failed to load claims retry data.'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [activeOnly, fetchJson, payerFilter, providerFilter, statusFilter, strategyFilter, tr],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const visibleRows = useMemo(() => {
    if (!data) return [];
    const query = search.trim().toLowerCase();
    return data.rows.filter((row) => {
      if (!query) return true;
      return (
        row.invoiceNumber.toLowerCase().includes(query) ||
        row.patient.toLowerCase().includes(query) ||
        row.payer.toLowerCase().includes(query) ||
        row.provider.toLowerCase().includes(query) ||
        row.issueCode.toLowerCase().includes(query) ||
        row.issueSummary.toLowerCase().includes(query)
      );
    });
  }, [data, search]);

  useEffect(() => {
    if (!visibleRows.length) {
      setSelectedId('');
      return;
    }
    setSelectedId((current) => (current && visibleRows.some((row) => row.id === current) ? current : visibleRows[0].id));
  }, [visibleRows]);

  const selectedRow = useMemo(
    () => visibleRows.find((row) => row.id === selectedId) ?? data?.rows.find((row) => row.id === selectedId) ?? null,
    [data?.rows, selectedId, visibleRows],
  );

  const recoveryOptions = useMemo<Highcharts.Options>(() => {
    if (!data) return chartBase('Recovery trend');
    return {
      ...chartBase('Recovery trend'),
      chart: { ...(chartBase('Recovery trend').chart || {}), type: 'line', height: 300 },
      xAxis: {
        ...(chartBase('Recovery trend').xAxis as Highcharts.XAxisOptions),
        categories: data.charts.recoveryTrend.map((point) => point.label),
      },
      series: [
        { type: 'line', name: 'Recovered', data: data.charts.recoveryTrend.map((point) => point.recovered), color: '#16a34a' },
        { type: 'line', name: 'At risk', data: data.charts.recoveryTrend.map((point) => point.atRisk), color: '#dc2626' },
      ],
    };
  }, [data]);

  const strategyOptions = useMemo<Highcharts.Options>(() => {
    if (!data) return chartBase('Strategy load');
    return {
      ...chartBase('Strategy load'),
      chart: { ...(chartBase('Strategy load').chart || {}), type: 'column', height: 300 },
      xAxis: {
        ...(chartBase('Strategy load').xAxis as Highcharts.XAxisOptions),
        categories: data.charts.strategyMix.map((entry) => strategyLabel[entry.strategy as ClaimRow['strategy']] || entry.strategy),
      },
      series: [
        { type: 'column', name: 'At-risk amount', data: data.charts.strategyMix.map((entry) => entry.amount), color: '#2563eb' },
        { type: 'column', name: 'Claims', data: data.charts.strategyMix.map((entry) => entry.count), color: '#7c3aed', yAxis: 1 },
      ],
      yAxis: [
        { title: { text: undefined }, labels: { style: { color: '#64748b' } }, gridLineColor: '#e2e8f0' },
        { title: { text: undefined }, labels: { style: { color: '#64748b' } }, opposite: true, gridLineWidth: 0 },
      ],
    };
  }, [data]);

  const payerOptions = useMemo<Highcharts.Options>(() => {
    if (!data) return chartBase('Top payers at risk');
    return {
      ...chartBase('Top payers at risk'),
      chart: { ...(chartBase('Top payers at risk').chart || {}), type: 'bar', height: 300 },
      xAxis: {
        ...(chartBase('Top payers at risk').xAxis as Highcharts.XAxisOptions),
        categories: data.charts.payerRisk.map((entry) => entry.payer),
      },
      series: [{ type: 'bar', name: 'At-risk balance', data: data.charts.payerRisk.map((entry) => entry.amount), color: '#f97316' }],
    };
  }, [data]);

  async function runBulkRetry() {
    if (!visibleRows.length) return;
    setActing('bulk-retry');
    setError(null);
    setBanner(null);
    try {
      const response = await fetchApi('/billing/claims-retry/run-smart-retry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: visibleRows.filter((row) => row.status === 'queued' || row.status === 'manual_review').map((row) => row.id) }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || tr('Unable to run smart retry.'));
      }
      setBanner(tr('Smart retry started for the visible queue.'));
      await loadData(true);
    } catch (err: any) {
      setError(err?.message || tr('Unable to run smart retry.'));
    } finally {
      setActing(null);
    }
  }

  async function runClaimAction(action: 'run-smart-retry' | 'submit' | 'manual-review' | 'requeue' | 'dismiss') {
    if (!selectedRow) return;
    setActing(action);
    setError(null);
    setBanner(null);
    try {
      const response = await fetchApi(`/billing/claims-retry/${selectedRow.id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || tr('Unable to update claim state.'));
      }
      setBanner(tr('Claim queue updated.'));
      await loadData(true);
    } catch (err: any) {
      setError(err?.message || tr('Unable to update claim state.'));
    } finally {
      setActing(null);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-wide text-slate-400">{tr('Financial')}</p>
          <h1 className="text-2xl font-semibold text-ink">{tr('Claims Retry Engine')}</h1>
          <p className="max-w-3xl text-sm text-slate-500">
            {tr('Live retry queue built from real insured invoices, with smart retry actions, manual appeal routing, and recovery tracking from the billing ledger.')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => loadData(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300"
            disabled={refreshing}
          >
            {refreshing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            {tr('Refresh')}
          </button>
          <button
            type="button"
            onClick={runBulkRetry}
            className="inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2 text-sm font-semibold text-white shadow-soft disabled:opacity-60"
            disabled={!visibleRows.length || acting !== null}
          >
            {acting === 'bulk-retry' ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
            {tr('Run smart retry')}
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <MetricCard title={tr('Queued denials')} value={String(data?.summary.queueCount || 0)} tone="bg-amber-50 text-amber-700" icon={<Clock3 size={18} />} />
        <MetricCard title={tr('Retrying now')} value={String(data?.summary.retryingCount || 0)} tone="bg-sky-50 text-sky-700" icon={<RefreshCw size={18} />} />
        <MetricCard title={tr('Manual review')} value={String(data?.summary.manualReviewCount || 0)} tone="bg-rose-50 text-rose-700" icon={<ShieldAlert size={18} />} />
        <MetricCard title={tr('Recovered 30d')} value={formatCurrencyCompact(data?.summary.recoveredAmount30d || 0)} tone="bg-emerald-50 text-emerald-700" icon={<CheckCircle2 size={18} />} />
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-ink">{tr('Queue filters')}</h2>
            <p className="text-sm text-slate-500">{tr('Filter the live queue by state, strategy, payer, provider, and search text.')}</p>
          </div>
          <div className="text-xs text-slate-400">
            {data?.generatedAt ? `${tr('Updated')} ${new Date(data.generatedAt).toLocaleString()}` : ''}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-[180px_200px_220px_240px_180px_minmax(0,1fr)]">
          <label className="block text-sm">
            <span className="mb-1.5 block font-medium text-slate-700">{tr('Status')}</span>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-300"
            >
              <option value="">{tr('All statuses')}</option>
              <option value="queued">{tr('Queued')}</option>
              <option value="retrying">{tr('Retrying')}</option>
              <option value="submitted">{tr('Submitted')}</option>
              <option value="manual_review">{tr('Manual review')}</option>
              <option value="recovered">{tr('Recovered')}</option>
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1.5 block font-medium text-slate-700">{tr('Strategy')}</span>
            <select
              value={strategyFilter}
              onChange={(event) => setStrategyFilter(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-300"
            >
              <option value="">{tr('All strategies')}</option>
              <option value="doc-fix">{tr('Documentation fix')}</option>
              <option value="coding-fix">{tr('Coding fix')}</option>
              <option value="appeal">{tr('Formal appeal')}</option>
              <option value="payer-follow-up">{tr('Payer follow-up')}</option>
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1.5 block font-medium text-slate-700">{tr('Payer')}</span>
            <select
              value={payerFilter}
              onChange={(event) => setPayerFilter(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-300"
            >
              <option value="">{tr('All payers')}</option>
              {data?.options.payers.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1.5 block font-medium text-slate-700">{tr('Provider')}</span>
            <select
              value={providerFilter}
              onChange={(event) => setProviderFilter(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-300"
            >
              <option value="">{tr('All providers')}</option>
              {data?.options.providers.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-ink"
              checked={activeOnly}
              onChange={(event) => setActiveOnly(event.target.checked)}
            />
            <div>
              <div className="font-medium text-ink">{tr('Active only')}</div>
              <div className="text-xs text-slate-500">{tr('Hide recovered and dismissed claims')}</div>
            </div>
          </label>

          <label className="block text-sm">
            <span className="mb-1.5 flex items-center gap-2 font-medium text-slate-700">
              <Search size={14} />
              {tr('Search queue')}
            </span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={tr('Invoice, patient, payer, issue...')}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-300"
            />
          </label>
        </div>
      </section>

      {banner ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{banner}</div> : null}
      {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
          <span className="inline-flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            {tr('Loading claims retry queue...')}
          </span>
        </div>
      ) : !data ? (
        <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500 shadow-sm">
          {tr('No claims retry data is available yet.')}
        </div>
      ) : (
        <>
          <section className="grid grid-cols-1 gap-5 2xl:grid-cols-3">
            <ChartCard title={tr('Recovery trend')}>
              <HighchartsReact highcharts={Highcharts} options={recoveryOptions} />
            </ChartCard>
            <ChartCard title={tr('Strategy load')}>
              <HighchartsReact highcharts={Highcharts} options={strategyOptions} />
            </ChartCard>
            <ChartCard title={tr('Top payers at risk')}>
              <HighchartsReact highcharts={Highcharts} options={payerOptions} />
            </ChartCard>
          </section>

          <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[minmax(0,1fr)_360px]">
            <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-4">
                <div>
                  <h2 className="text-lg font-semibold text-ink">{tr('Live retry queue')}</h2>
                  <p className="text-sm text-slate-500">{visibleRows.length.toLocaleString()} {tr('claims visible')}</p>
                </div>
                <div className="text-xs text-slate-400">{formatCurrency(data.summary.atRiskAmount)} {tr('currently at risk')}</div>
              </div>

              <div className="max-h-[760px] overflow-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 border-b border-slate-100 bg-white text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-6 py-3">{tr('Invoice')}</th>
                      <th className="px-4 py-3">{tr('Patient')}</th>
                      <th className="px-4 py-3">{tr('Payer')}</th>
                      <th className="px-4 py-3">{tr('Issue')}</th>
                      <th className="px-4 py-3">{tr('Strategy')}</th>
                      <th className="px-4 py-3 text-right">{tr('Balance')}</th>
                      <th className="px-4 py-3 text-center">{tr('Age')}</th>
                      <th className="px-4 py-3">{tr('Confidence')}</th>
                      <th className="px-6 py-3">{tr('Status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {visibleRows.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-6 py-10 text-center text-sm text-slate-500">
                          {tr('No claims match the current filters.')}
                        </td>
                      </tr>
                    ) : (
                      visibleRows.map((row) => {
                        const selected = row.id === selectedRow?.id;
                        return (
                          <tr
                            key={row.id}
                            onClick={() => setSelectedId(row.id)}
                            className={`cursor-pointer transition ${selected ? 'bg-sky-50/70' : 'hover:bg-slate-50'}`}
                          >
                            <td className="px-6 py-4">
                              <div className="font-semibold text-ink">{row.invoiceNumber}</div>
                              <div className="text-[11px] text-slate-400">{row.provider}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="font-medium text-ink">{row.patient}</div>
                              <div className="text-xs text-slate-500">{row.mrn}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="font-medium text-ink">{row.payer}</div>
                              <div className="text-xs text-slate-500">{row.serviceType}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="font-semibold text-ink">{row.issueCode}</div>
                              <div className="max-w-[280px] text-xs text-slate-500">{row.issueSummary}</div>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${strategyTone[row.strategy]}`}>
                                {strategyLabel[row.strategy]}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-right font-semibold text-rose-700">{formatCurrency(row.balance)}</td>
                            <td className="px-4 py-4 text-center">
                              <div className="font-semibold text-ink">{row.ageDays}</div>
                              <div className="text-[11px] text-slate-400">{row.pastDueDays} {tr('past due')}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="font-semibold text-ink">{formatPercent(row.confidence)}</div>
                              <div className="text-[11px] text-slate-400">{row.retryCount} {tr('retries')}</div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusTone[row.status]}`}>
                                {row.status.replace('_', ' ')}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <aside className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              {selectedRow ? (
                <div className="space-y-5">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">{tr('Focused claim')}</div>
                    <h2 className="mt-2 text-xl font-semibold text-ink">{selectedRow.invoiceNumber}</h2>
                    <p className="text-sm text-slate-500">
                      {selectedRow.patient} • {selectedRow.payer}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                    <MetricLine label={tr('Issue code')} value={selectedRow.issueCode} detail={selectedRow.issueSummary} />
                    <MetricLine label={tr('Strategy')} value={strategyLabel[selectedRow.strategy]} />
                    <MetricLine label={tr('Status')} value={selectedRow.status.replace('_', ' ')} />
                    <MetricLine label={tr('Outstanding balance')} value={formatCurrency(selectedRow.balance)} />
                    <MetricLine label={tr('Billed')} value={formatCurrency(selectedRow.billed)} />
                    <MetricLine label={tr('Paid')} value={formatCurrency(selectedRow.paid)} />
                    <MetricLine label={tr('Confidence')} value={formatPercent(selectedRow.confidence)} />
                    <MetricLine label={tr('Age')} value={`${selectedRow.ageDays} ${tr('days')}`} />
                    <MetricLine label={tr('Past due')} value={`${selectedRow.pastDueDays} ${tr('days')}`} />
                    <MetricLine label={tr('Next follow-up')} value={formatDateTime(selectedRow.nextFollowUpAt)} />
                    <MetricLine label={tr('Last action')} value={formatDateTime(selectedRow.lastActionAt)} detail={selectedRow.latestActionSummary || undefined} />
                    <MetricLine label={tr('Recovered amount')} value={formatCurrency(selectedRow.recoveredAmount)} />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <StatCard title={tr('Retries run')} value={String(selectedRow.retryCount)} subtitle={selectedRow.lastPaymentDate ? formatDateTime(selectedRow.lastPaymentDate) : tr('No payment posted')} />
                    <StatCard title={tr('Follow-up state')} value={selectedRow.status.replace('_', ' ')} subtitle={selectedRow.resolvedAt ? formatDateTime(selectedRow.resolvedAt) : tr('Still active')} />
                  </div>

                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => runClaimAction('run-smart-retry')}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-4 py-2 text-sm font-semibold text-white shadow-soft disabled:opacity-60"
                      disabled={acting !== null || selectedRow.status === 'recovered' || selectedRow.status === 'dismissed'}
                    >
                      {acting === 'run-smart-retry' ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                      {tr('Run smart retry')}
                    </button>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => runClaimAction('submit')}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300"
                        disabled={acting !== null || selectedRow.status === 'recovered' || selectedRow.status === 'dismissed'}
                      >
                        <Send size={14} />
                        {tr('Submit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => runClaimAction('manual-review')}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300"
                        disabled={acting !== null || selectedRow.status === 'recovered' || selectedRow.status === 'dismissed'}
                      >
                        <ShieldAlert size={14} />
                        {tr('Manual review')}
                      </button>
                      <button
                        type="button"
                        onClick={() => runClaimAction('requeue')}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300"
                        disabled={acting !== null}
                      >
                        <RefreshCw size={14} />
                        {tr('Requeue')}
                      </button>
                      <button
                        type="button"
                        onClick={() => runClaimAction('dismiss')}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-200 bg-white px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
                        disabled={acting !== null || selectedRow.status === 'recovered'}
                      >
                        <ClipboardCheck size={14} />
                        {tr('Dismiss')}
                      </button>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-ink">
                      <ArrowUpRight size={16} />
                      {tr('Recommended focus')}
                    </div>
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                      {selectedRow.strategy === 'appeal'
                        ? tr('This claim has aged enough to justify a formal appeal path. Confirm payer narrative and attach the full packet.')
                        : selectedRow.strategy === 'doc-fix'
                        ? tr('The engine sees a documentation gap signal. Review chart attachments and missing support before resubmission.')
                        : selectedRow.strategy === 'coding-fix'
                        ? tr('This balance looks underpaid relative to posted reimbursement. Validate coding and contract assumptions before retry.')
                        : tr('Use direct payer status follow-up and keep this claim in motion until payment posts or the queue is formally dismissed.')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500">{tr('Select a claim to inspect its retry workflow and actions.')}</div>
              )}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}

function MetricCard({ title, value, tone, icon }: { title: string; value: string; tone: string; icon: ReactNode }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-4">
        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tone}`}>{icon}</div>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">{title}</p>
          <p className="mt-2 text-2xl font-semibold text-ink">{value}</p>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 text-sm font-semibold text-ink">{title}</div>
      <div className="min-h-[300px]">{children}</div>
    </section>
  );
}

function MetricLine({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="py-2">
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 text-sm font-semibold text-ink">{value}</div>
      {detail ? <div className="mt-1 text-xs text-slate-500">{detail}</div> : null}
    </div>
  );
}

function StatCard({ title, value, subtitle }: { title: string; value: string; subtitle: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-400">{title}</div>
      <div className="mt-2 text-2xl font-semibold text-ink">{value}</div>
      <div className="mt-1 text-xs text-slate-500">{subtitle}</div>
    </div>
  );
}
